import React from 'react';
import PropTypes from 'prop-types';
import { getHexTextColorHex } from 'library/colors';
import { durationString } from 'library/time';
import HeaderBar from 'components/headerBar/header-bar';
import {
  Alert, Collapse, Icon, Tabs, Tag
} from 'antd';
import { CustomFilter, ActivityCustomFilter } from 'components/Filtering';
import { Options, Picker } from 'components/Timeline';
import { Permissions } from 'components/Permissions';

const { TabPane } = Tabs;

const displayDefaultInfo = () => (
  <div className="bhp-details">
    <Icon type="info-circle-o" />
    {' '}
    Please select an item to add to the timeline
  </div>
);

const displayActivityInfo = activity => (
  <div className="bhp-details bhp-activity-info">
    {!activity.message && (
      <>
        <div>
          <strong>
            Activity:
          </strong>
          <span>
            <Tag
              color={activity.color}
              style={{ color: getHexTextColorHex(activity.color) }}
            >
              {`${activity.name} ${activity.activity_type.code ? `(${activity.activity_type.code})` : ''}`}
            </Tag>
          </span>
        </div>
        <div>
          <strong>
            Duration:
          </strong>
          <span>
            {durationString(activity.duration)}
          </span>
        </div>
      </>
    )}
    {activity.message && (
      <Alert
        banner
        showIcon
        message={activity.message}
        type="error"
        closable={false}
      />
    )}
  </div>
);

const displayTemplateInfo = template => (
  <div className="bhp-details bhp-template-info">
    <div>
      <strong>
        Template:
      </strong>
      <span>
        {template.name}
      </span>
    </div>
    <div>
      <strong>
        Activities:
      </strong>
      <span>
        {template.activities.length}
      </span>
    </div>
  </div>
);

const displaySelectedItem = (item) => {
  const { type, data } = item || {};
  switch (type) {
  case 'activity':
    return displayActivityInfo(data);

  case 'template':
    return displayTemplateInfo(data);

  default:
    return displayDefaultInfo();
  }
};

class Sidebar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedItem: null,
      selectedActivity: null,
      selectedTemplate: null
    };

    this.handleTabChange = this.handleTabChange.bind(this);

    this.handleActivityPickerRowClick = this.handleActivityPickerRowClick.bind(this);
    this.handleTemplatePickerRowClick = this.handleTemplatePickerRowClick.bind(this);

    this.handlePinActivity = this.handlePinActivity.bind(this);

    this.handleActivityFilterChange = this.handleActivityFilterChange.bind(this);
    this.handleUserFilterChange = this.handleUserFilterChange.bind(this);

    this.getUserFilter = this.getUserFilter.bind(this);
    this.getActivityFilter = this.getActivityFilter.bind(this);
    this.getOptions = this.getOptions.bind(this);
    this.getOptionsTabSettings = this.getOptionsTabSettings.bind(this);
    this.getActivityTabSettings = this.getActivityTabSettings.bind(this);
  }

  getOptionsTabSettings() {
    const { tabs } = this.props;

    return {
      ...tabs.optionFields,
      showActivityCreateCount: false,
      showActivityHoursBetween: false,
    };
  }

  getActivityTabSettings() {
    const { tabs } = this.props;

    return {
      ...tabs.optionFields,
      showTimelineMaxDays: false,
      showTimelineSnapMinutes: false,
      showDisplayTravelLayer: false,
      showDisplayLeaveLayer: false,
      showDisplayAvailabilityLayer: false,
      showRosterPivot: false,
      showHideEmptyRows: false,
      showAssignmentTitleFormat: false,
      showTimeFilter: false,
      showHideUnallocatedRows: tabs.optionFields?.showHideUnallocatedRows ?? false
    };
  }

  getUserFilter(input) {
    this.userFilter = input || this.userFilter;
  }

  getActivityFilter(input) {
    this.activityFilter = input || this.activityFilter;
  }

  getOptions(input) {
    this.timelineOptions = input || this.timelineOptions;
  }

  handleUserFilterChange(filters) {
    const { onFilterChange } = this.props;
    const user = { ...filters };
    onFilterChange({ user });
  }

  handleTabChange(activeKey) {
    const { onItemChange } = this.props;
    const { selectedItem } = this.state;
    // -- When the tab changes, update the selected item
    // -- based on the item picked in the tab list.
    // ---
    switch (activeKey) {
    case '1':
      this.setState(prevState => ({
        selectedItem: prevState.selectedActivity
      }),
      () => {
        onItemChange(selectedItem);
      });
      break;
    case '2':
      this.setState(prevState => ({
        selectedItem: prevState.selectedTemplate
      }),
      () => {
        onItemChange(selectedItem);
      });
      break;
    default:
      break;
    }
  }

  handleActivityPickerRowClick(data) {
    const { onItemChange } = this.props;
    if (data.active) {
      const item = {
        type: 'activity',
        data
      };
      this.setState({
        selectedItem: item,
        selectedActivity: item
      });
      onItemChange(item);
    }
    else {
      this.setState({
        selectedItem: {
          type: 'activity',
          data: {
            ...data,
            message: `${data.name} is inactive`
          }
        },
        selectedActivity: null
      });
    }
  }

  handleTemplatePickerRowClick(data) {
    const { onItemChange } = this.props;
    if (data.id) {
      const item = {
        type: 'template',
        data
      };
      this.setState({
        selectedItem: item,
        selectedTemplate: item
      });
      onItemChange(item);
    }
  }

  handleActivityFilterChange(filters) {
    const { onFilterChange } = this.props;
    const activity = { ...filters };
    onFilterChange({ activity });
  }

  handlePinActivity(activities) {
    const { onPinItem } = this.props;
    onPinItem(activities, 'activity');
  }

  render() {
    const { selectedItem } = this.state;
    const {
      tabs, config, defaultSettings, options, onOptionsChange, pinnedActivities, visibleTimeStart, visibleTimeEnd
    } = this.props;

    return (
      <div className="bhp-timeline-toolbar bhp-container">
        <Permissions.Consumer>
          {({ permissions }) => (
            <>
              <HeaderBar title="Timeline Options" capitalize={false} />
              {(tabs.showActivities || tabs.showTemplates) && (
                <div className="bhp-selected-item-container">
                  <div className="bhp-selected-item">
                    {displaySelectedItem(selectedItem)}
                  </div>
                </div>
              )}
              <Tabs defaultActiveKey="1" type="card" onChange={this.handleTabChange}>
                {tabs.showActivities && (
                  <TabPane key="1" tab="Activities">
                    <Collapse defaultActiveKey={['2']}>
                      <Collapse.Panel header="Options" key="1">
                        <Options
                          options={options}
                          optionFields={this.getActivityTabSettings()}
                          onUpdate={onOptionsChange}
                          ref={this.getOptions}
                          canAdmin={permissions.CanAdminRosters()
                            .hasPermissions()}
                        />
                      </Collapse.Panel>
                      <Collapse.Panel header="Activities" key="2">
                        <Picker
                          onRowClick={this.handleActivityPickerRowClick}
                          onPinItem={this.handlePinActivity}
                          pinnedItems={pinnedActivities}
                          {...config.pickers.activity}
                        />
                      </Collapse.Panel>
                    </Collapse>
                  </TabPane>
                )}
                {tabs.showTemplates && (
                  <TabPane key="2" tab="Templates">
                    <Picker
                      onRowClick={this.handleTemplatePickerRowClick}
                      {...config.pickers.template}
                    />
                  </TabPane>
                )}
                {tabs.showFilters && (
                  <TabPane key="3" tab="Filters">
                    {config.filters.showUserFilter && (
                      <CustomFilter
                        activeText="Hide inactive users"
                        dataSource={defaultSettings.filters.user}
                        endDate={visibleTimeEnd}
                        onUpdateCustomFilters={this.handleUserFilterChange}
                        placeholder={config.filters.user.placeholder}
                        reducerKey={config.filters.user.key}
                        ref={this.getUserFilter}
                        showActive
                        showClearButton={false}
                        startDate={visibleTimeStart}
                        themeName={config.filters.user.theme}
                        title="User Filter"
                      />
                    )}
                    {config.filters.showActivityFilter && (
                      <ActivityCustomFilter
                        dataSource={defaultSettings.filters.activity}
                        endDate={visibleTimeEnd}
                        onUpdateCustomFilters={this.handleActivityFilterChange}
                        placeholder={config.filters.activity.placeholder}
                        reducerKey={config.filters.activity.key}
                        ref={this.getActivityFilter}
                        showClearButton={false}
                        showActive
                        startDate={visibleTimeStart}
                        themeName={config.filters.activity.theme}
                        title="Activity Filter"
                        activeText="Hide inactive activities"
                      />
                    )}
                  </TabPane>
                )}
                {tabs.showOptions && (
                  <TabPane key="4" tab="Options">
                    <Options
                      options={options}
                      optionFields={this.getOptionsTabSettings()}
                      onUpdate={onOptionsChange}
                      ref={this.getOptions}
                      canAdmin={permissions.CanAdminRosters()
                        .hasPermissions()}
                    />
                  </TabPane>
                )}
              </Tabs>
            </>
          )}
        </Permissions.Consumer>
      </div>
    );
  }
}

Sidebar.propTypes = {
  config: PropTypes.object,
  defaultSettings: PropTypes.object,
  tabs: PropTypes.object,
  filters: PropTypes.object,
  options: PropTypes.object,
  onItemChange: PropTypes.func,
  onFilterChange: PropTypes.func,
  onPinItem: PropTypes.func,
  onOptionsChange: PropTypes.func,
  pinnedActivities: PropTypes.array,
  visibleTimeStart: PropTypes.object,
  visibleTimeEnd: PropTypes.object
};

Sidebar.defaultProps = {
  config: {},
  defaultSettings: {},
  tabs: {},
  filters: null,
  options: null,
  onItemChange() {
  },
  onFilterChange() {
  },
  onPinItem() {
  },
  onOptionsChange() {
  },
  pinnedActivities: [],
  visibleTimeStart: {},
  visibleTimeEnd: {}
};

export default Sidebar;
